import type { BuyOrder } from "api/types";

export type OrderReadiness = {
  order: BuyOrder;
  /** Qué falta para poder enviarla. Vacío = lista. */
  issues: string[];
  lineCount: number;
};

/**
 * Chequeo previo al envío, con las mismas reglas que
 * `integrations/profit_purchasing/payload.py`.
 *
 * **El backend sigue siendo la autoridad**: esto no lo reemplaza, lo adelanta.
 * Sin este paso, una orden sin proveedor se descubre recién en la respuesta del
 * `POST`, después de haber materializado el documento — y el usuario ve un 422
 * sin saber que la solución era prender el panel de datos de compra.
 *
 * Los textos se mantienen alineados con los del backend a propósito: que el
 * mismo problema se lea igual venga de donde venga.
 */
export function checkProfitReadiness(order: BuyOrder): OrderReadiness {
  const issues: string[] = [];
  const lines = order.incoming_product_list ?? [];

  if (!String(order.vendor_code ?? "").trim()) {
    issues.push("Falta el proveedor de la orden.");
  }
  if (lines.length === 0) {
    issues.push("La orden no tiene renglones.");
  }

  const sinAlmacen: string[] = [];
  const sinCosto: string[] = [];
  const sinCantidad: string[] = [];
  lines.forEach((line) => {
    const code = String(line.product_code ?? "").trim() || "(sin código)";
    if (!String(line.warehouse_code ?? "").trim()) {
      sinAlmacen.push(code);
    }
    if (line.unit_cost == null || Number(line.unit_cost) <= 0) {
      sinCosto.push(code);
    }
    const units =
      line.total_units != null
        ? Number(line.total_units)
        : Number(line.bulks) * Number(line.units_per_bulk ?? 0);
    if (!units || units <= 0) {
      sinCantidad.push(code);
    }
  });

  // Se agrupan los materiales por problema en vez de emitir una línea por cada
  // uno: una orden de 40 renglones sin costo produciría 40 mensajes idénticos.
  const resumen = (codes: string[], texto: string) => {
    if (codes.length === 0) {
      return;
    }
    const muestra = codes.slice(0, 5).join(", ");
    const resto = codes.length > 5 ? ` y ${codes.length - 5} más` : "";
    issues.push(`${texto}: ${muestra}${resto}.`);
  };
  resumen(sinAlmacen, "Falta el almacén de destino");
  resumen(sinCosto, "Falta el costo unitario");
  resumen(sinCantidad, "Falta la cantidad");

  return { order, issues, lineCount: lines.length };
}
